/**
 * Warehouse Reports (SeedTrac RSW "Reports" tab; docs/designs/rsw-warehouse-reports.md).
 *
 * One definition per report: its filters, its columns and how its rows are grouped. The API builds
 * the JSON, the CSV and the PDF from it and the web table renders from it, so the three can never
 * disagree on a header or a total. Column lists are transcribed from the legacy screenshots in
 * `RSW Module.xlsx` (image5–10) and its "Required Report In RSW" sheet; `warehouse-reports.test.ts`
 * pins them.
 */
import { ARRIVAL_REPORT_DEFS, type ArrivalReportKey } from "./arrival-reports";
import {
  HIGH_MOISTURE_REPORTS,
  type HighMoistureReportKey,
} from "./moisture-alerts";
import {
  PACKAGING_REPORTS,
  type PackagingReportKey,
} from "./packaging-reports";
import type { LotQcStatus } from "./qc";
import { DISPATCH_REPORTS, type DispatchReportKey } from "./dispatch-reports";
import { QUALITY_REPORTS, type QualityReportKey } from "./quality-reports";
import { STOCK_REPORTS, type StockReportKey } from "./stock-reports";

export const WAREHOUSE_REPORT_KEYS = [
  // The legacy RSW Reports tab, in its menu order (wmsd rsw/index1.php, read 2026-10-16).
  "quality",
  "crop-variety",
  "substandard",
  "unidentified",
  "coded",
  "reserve",
  "warehouse-wise",
  "daily-qc",
  "daily-got",
  "qc-ageing",
  // Not in the legacy RSW tab.
  "periodical-got",
  // Not in the RSW Reports tab: where seed comes from (user, 2026-10-08).
] as const;
export type WarehouseReportKey = (typeof WAREHOUSE_REPORT_KEYS)[number];

export function isWarehouseReportKey(k: string): k is WarehouseReportKey {
  return (WAREHOUSE_REPORT_KEYS as readonly string[]).includes(k);
}

/**
 * `sectioned`: one block per Crop | Variety with a subtotal. `aggregated`: one row per crop ×
 * variety, already a total. `flat`: one list, no headings.
 */
export type ReportKind = "sectioned" | "aggregated" | "flat";

/** `period` is a From/To pair; `date` a single day. */
export type ReportFilter =
  | "crop"
  | "variety"
  | "date"
  | "period"
  /** Arrival reports (arrival-reports.ts): the arrival type and the legacy PM pickers. */
  | "arrivalType"
  | "organiser"
  | "state"
  | "prodLocation"
  | "personnel"
  | "sourcePlant"
  | "vendor"
  /** CSW Warehouse wise: WH (required), Bin, Sub-Bin. */
  | "location"
  /** CSW Crop Variety wise: the legacy "With SLOC" and "With Harvest & Arrival Date" radios. */
  | "withSloc"
  | "withDates"
  /** CSW QC Ageing: "Duration since last QC test" (more than N days). */
  | "qcAge"
  /** RSW Coded: the legacy "Age" select (ALL / more than 60 / more than 90 days since arrival). */
  | "codedAge"
  /** Packaging reports (packaging-reports.ts): the UPS (pack size) and "Pack QC Status" pickers. */
  | "ups"
  | "packQc"
  /** Packaging QC Ageing: the legacy duration, a band (Select) or a comparison with N days (Fill). */
  | "packAge"
  /** Periodical Packing: Summary or Detail (lot-wise). */
  | "packingView"
  /** Periodical Packing: the legacy "With Re-Printing" tick (re-printed lots counted too). */
  | "withReprint"
  /** Stock reports (stock-reports.ts): the day the stock is counted on (today unless chosen). */
  | "asOnDate"
  /** Stock reports: the crop's vertical (Veg Crop, Field Crop, …). */
  | "vertical"
  /** Periodical Dispatch: the plant the dispatch went to (`ReportOptions.plants`). */
  | "destPlant"
  /** Stock reports: the variety's classification (Hybrid, OP, …; "Not set" when it has none). */
  | "classification";

/** The search keys a report's own pickers (`ReportDef.choices`) write. */
export const REPORT_CHOICE_PARAMS = ["kind", "mode", "seedStage"] as const;
export type ReportChoiceParam = (typeof REPORT_CHOICE_PARAMS)[number];

/**
 * A report's own picker, one of the legacy screen's radios or selects (Arrival type, Dispatch type,
 * Seed Stage, QC Test Instance …). Its first option is the default and is never sent.
 */
export interface ReportChoice {
  param: ReportChoiceParam;
  label: string;
  options: readonly { value: string; label: string }[];
}

export type ReportCellFormat =
  "text" | "int" | "qty" | "pct" | "date" | "slocs";

export interface ReportColumn {
  key: string;
  label: string;
  /** A second header row above this column ("QC", "GOT"). */
  group?: string;
  align: "left" | "center" | "right";
  format: ReportCellFormat;
  /** Summed into section and report totals. */
  total?: boolean;
  /**
   * No source in nexseed yet (manual-map gap G22): kept so the layout matches the legacy screen,
   * always printed blank.
   */
  blank?: true;
  /**
   * Left empty on purpose for the lab to fill by hand on the printout (Evaluated by, Receiving SCAN;
   * user, 2026-11-01): printed blank and wide, with no "not recorded" note.
   */
  writeIn?: true;
}

/** Reports outside the RSW desk that reuse the same table, CSV, PDF and Excel builders. */
export type ReportKey =
  | WarehouseReportKey
  | CswReportKey
  | ArrivalReportKey
  | PackagingReportKey
  | StockReportKey
  | HighMoistureReportKey
  | QualityReportKey
  | DispatchReportKey;

export interface ReportDef {
  key: ReportKey;
  /** Menu label, in the legacy Reports tab's words. */
  menu: string;
  /** Heading on the page and the PDF. "As on date" reports print today's date after it. */
  title: string;
  asOn: boolean;
  kind: ReportKind;
  filters: readonly ReportFilter[];
  columns: readonly ReportColumn[];
  /** Said when the filters match more rows than a report may hold (`narrow_filters`). */
  tooMany: string;
  /** What a sectioned report's two heading parts are called; Crop and Variety unless set. */
  sectionLabels?: readonly [string, string];
  /** Shown under the PDF in place of the default note on blank columns. */
  note?: string;
  /** The report's own pickers, after the crop and variety. */
  choices?: readonly ReportChoice[];
}

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const SERIAL = c("serial", "#", "int", { align: "center" });
const LOT = c("lotNumber", "Lot No.", "text");
/** The lot's SP codes: F and M under one "SP Code" heading, as the Unloading Sheet heads them (user, 2026-10-26). */
const SP_CODE = [
  c("spFemale", "F", "text", { group: "SP Code", align: "center" }),
  c("spMale", "M", "text", { group: "SP Code", align: "center" }),
];
const NOB = c("bags", "NoB", "int", { total: true });
const QTY = c("qty", "Qty", "qty", { total: true });
const SLOC = c("slocs", "SLOC", "slocs");
const QC = c("qcStatus", "QC Status", "text", { align: "center" });
const MOIST = c("moisturePct", "Moist %", "pct");
const GERM = c("germinationPct", "Germ %", "pct");
const DOT = c("testedOn", "DoT", "date");
const GOT = c("gotStatus", "GOT Status", "text", { align: "center" });
const SEED = c("seedStatus", "Seed Status", "text", { align: "center" });
/**
 * "Yes" for a lot a Priming slip returned, or one made from it (`lots.primed_at`; user,
 * 2026-10-27). Every lot-by-lot stock report prints it after Seed Status, else after GOT Status.
 */
const PRIMED = c("primed", "Primed", "text", { align: "center" });
const LOTS_TOO_MANY = "Too many lots. Choose a crop or variety.";
/** From the reviewed GOT result (`got_samples`) of the lot's lineage. */
const GOT_GRADE = c("gotGrade", "GOT Grade", "text", { align: "center" });
const GOT_DOGR = c("dogr", "DOGR", "date");
const GP = c("geneticPurityPct", "Genetic Purity %", "pct");
/** Every lot-by-lot report says when the lot arrived (user, 2026-10-18). */
const ARRIVED = c("arrivedOn", "Date of Arrival", "date");
/** The arrival's Transaction ID, as the Arrival reports head it (RSW Crop Variety, user 2026-10-23). */
const TXN = c("docNo", "Transaction ID", "text");
/**
 * The lot's verified Physical Purity reading, the figures Arrivals › PP Appearance showed (RSW Crop
 * Variety wise, user 2026-10-26). Blank until the QC Supervisor verifies a reading.
 */
const PP_FIGURES = [
  c("ppPurePct", "Pure %", "pct"),
  c("ppInertPct", "Inert %", "pct"),
  c("ppLightPct", "Light %", "pct"),
  c("ppDiscolouredPct", "Discol. %", "pct"),
  c("ppCutPct", "Cut %", "pct"),
  c("ppSmallPct", "Small %", "pct"),
  c("ppRoughPct", "Rough %", "pct"),
  c("ppWhitePct", "White %", "pct"),
  c("ppOcsPerKg", "OCS /kg", "int"),
  c("ppOdvPerKg", "ODV /kg", "int"),
];
const PP_FIGURE_KEYS = new Set(PP_FIGURES.map((col) => col.key));
const GOT_NOTE =
  "GOT status is the reviewed GOT result where the lot's lineage has one, else the status declared at arrival.";

/** The legacy Coded report's "Age" choices: more than this many days since arrival. */
export const CODED_AGE_DAYS = [60, 90] as const;

/** An RSW report's definition: its key is one of the desk's. */
export type WarehouseReportDef = ReportDef & { key: WarehouseReportKey };

export const WAREHOUSE_REPORTS: {
  [K in WarehouseReportKey]: ReportDef & { key: K };
} = {
  quality: {
    key: "quality",
    menu: "Quality Based Raw Seed Report",
    title: "Quality Based Raw Seed Stock Report",
    asOn: true,
    kind: "aggregated",
    filters: ["crop", "variety"],
    columns: [
      SERIAL,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("qty", "Total Qty", "qty", { total: true }),
      c("qcOk", "OK", "qty", { group: "QC", total: true }),
      c("qcUt", "UT", "qty", { group: "QC", total: true }),
      // Borderline QC and GOT, out of UT so each group still adds up to Total Qty (user, 2026-10-26).
      c("qcBl", "BL", "qty", { group: "QC", total: true }),
      c("qcFail", "Fail", "qty", { group: "QC", total: true }),
      c("gotNut", "NUT", "qty", { group: "GOT", total: true }),
      c("gotOk", "OK", "qty", { group: "GOT", total: true }),
      c("gotUt", "UT", "qty", { group: "GOT", total: true }),
      c("gotBl", "BL", "qty", { group: "GOT", total: true }),
      c("gotFail", "Fail", "qty", { group: "GOT", total: true }),
      // No Primed column (user, 2026-11-03: "remove the primed column").
    ],
    tooMany: LOTS_TOO_MANY,
  },
  "crop-variety": {
    key: "crop-variety",
    menu: "Crop Variety Wise Seed Report",
    title: "Crop Variety wise Raw Seed Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety", "withSloc", "withDates"],
    columns: [
      SERIAL,
      TXN,
      LOT,
      NOB,
      QTY,
      SLOC,
      QC,
      MOIST,
      GERM,
      ...PP_FIGURES,
      DOT,
      GOT,
      c("productionLocation", "Production Location", "text"),
      c("state", "State", "text"),
      SEED,
      PRIMED,
      c("productionGrade", "Prod. Grade", "text", { align: "center" }),
      GOT_GRADE,
      // Date of Arrival sits just before Harvest Date (user, 2026-10-24).
      ARRIVED,
      c("harvestedOn", "Harvest Date", "date"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: GOT_NOTE,
  },
  substandard: {
    key: "substandard",
    menu: "Substandard Seed Report",
    title: "Crop Variety wise Substandard Raw Seed Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety"],
    columns: [
      SERIAL,
      ARRIVED,
      LOT,
      NOB,
      QTY,
      SLOC,
      c("qcStatus", "QC status", "text", { align: "center" }),
      MOIST,
      GERM,
      DOT,
      GOT,
      GP,
      GOT_DOGR,
      SEED,
      PRIMED,
    ],
    tooMany: LOTS_TOO_MANY,
    note: GOT_NOTE,
  },
  unidentified: {
    key: "unidentified",
    menu: "Unidentified Raw Seed Report",
    title: "Unidentified Raw Seed Report",
    asOn: true,
    kind: "flat",
    filters: ["crop"],
    columns: [
      SERIAL,
      c("arrivedOn", "Date of Arrival", "date"),
      c("crop", "Crop", "text"),
      c("lotNumber", "Lot Number", "text"),
      NOB,
      QTY,
      c("qcStatus", "QC status", "text", { align: "center" }),
      GOT,
      PRIMED,
    ],
    tooMany: "Too many lots. Choose a crop.",
    note: "Raw seed in stock with no variety and no SP code. " + GOT_NOTE,
  },
  coded: {
    key: "coded",
    menu: "Coded Raw Seed Report",
    title: "Coded Raw Seed Report",
    asOn: true,
    kind: "flat",
    filters: ["codedAge"],
    columns: [
      SERIAL,
      c("arrivedOn", "Date of Arrival", "date"),
      c("crop", "Crop", "text"),
      // Coded lots have no variety yet: their SP codes stand in for it (user, 2026-10-17 + 10-21 + 10-26).
      ...SP_CODE,
      LOT,
      NOB,
      QTY,
      QC,
      GOT,
      PRIMED,
      c("duration", "Duration (Y M D)", "text", { align: "center" }),
    ],
    tooMany: "Too many lots. Choose an age.",
    note: GOT_NOTE,
  },
  reserve: {
    key: "reserve",
    menu: "Reserve Raw Seed Report",
    title: "Reserve Seed Report",
    asOn: true,
    kind: "flat",
    filters: ["crop", "variety"],
    columns: [
      SERIAL,
      ARRIVED,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      LOT,
      NOB,
      QTY,
      c("qcStatus", "QC status", "text", { align: "center" }),
      MOIST,
      GERM,
      c("testedOn", "DOT", "date"),
      GOT,
      SEED,
      PRIMED,
      c("reserveComment", "Reserve Comment", "text"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: GOT_NOTE,
  },
  "warehouse-wise": {
    key: "warehouse-wise",
    menu: "Warehouse wise Report",
    title: "Warehouse wise Report",
    asOn: true,
    kind: "sectioned",
    sectionLabels: ["WH", "Bin"],
    filters: ["location"],
    columns: [
      c("subBin", "Subbin", "text", { align: "center" }),
      LOT,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("stage", "Stage", "text", { align: "center" }),
      NOB,
      QTY,
      c("qcStatus", "QC status", "text", { align: "center" }),
      DOT,
      MOIST,
      GERM,
      GOT,
      SEED,
      PRIMED,
    ],
    tooMany: "Too many lots. Choose a bin.",
    note: GOT_NOTE,
  },
  "daily-qc": {
    key: "daily-qc",
    menu: "Daily QC Result Report",
    title: "Daily QC Result Report",
    asOn: false,
    kind: "flat",
    filters: ["date", "crop"],
    columns: [
      SERIAL,
      ARRIVED,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      LOT,
      NOB,
      QTY,
      c("ppVerdict", "PP", "text", { align: "center" }),
      MOIST,
      GERM,
      c("testedOn", "DOT", "date"),
      QC,
      GOT_DOGR,
      GOT,
      PRIMED,
    ],
    tooMany: "Too many results on one day. Choose a crop.",
    note: GOT_NOTE,
  },
  "daily-got": {
    key: "daily-got",
    menu: "Daily GOT Result Report",
    title: "Daily GOT Result Report",
    asOn: false,
    kind: "flat",
    filters: ["date", "crop"],
    columns: [
      SERIAL,
      ARRIVED,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      LOT,
      NOB,
      QTY,
      c("ppVerdict", "PP", "text", { align: "center" }),
      MOIST,
      GERM,
      c("testedOn", "DOT", "date"),
      QC,
      GOT_DOGR,
      GOT,
      PRIMED,
    ],
    tooMany: "Too many results on one day. Choose a crop.",
    note: "Raw seed in stock whose lineage had a GOT result reviewed on this day.",
  },
  "qc-ageing": {
    key: "qc-ageing",
    menu: "QC Ageing Status Report",
    title: "QC Test Ageing Status Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety", "qcAge"],
    columns: [
      SERIAL,
      ARRIVED,
      LOT,
      NOB,
      QTY,
      GERM,
      c("testedOn", "DOT", "date"),
      c("daysSinceDot", "Days since DoT", "int"),
      QC,
      GOT_DOGR,
      GOT,
      PRIMED,
      SLOC,
    ],
    tooMany: LOTS_TOO_MANY,
    note: "Lots whose last QC result is OK. " + GOT_NOTE,
  },
  "periodical-got": {
    key: "periodical-got",
    menu: "Periodical GOT Report Crop Variety Date Wise",
    title: "Periodical GOT Report — Crop Variety Date wise",
    asOn: false,
    kind: "sectioned",
    filters: ["period", "crop", "variety"],
    columns: [
      SERIAL,
      c("arrivedOn", "Date of Arrival", "date"),
      c("days", "Days", "int"),
      c("frnNo", "FRN No.", "text"),
      LOT,
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      ...SP_CODE,
      c("productionPerson", "Prod. Person", "text"),
      c("productionLocation", "Prod. Location", "text"),
      c("organiser", "Organizer", "text"),
      c("farmer", "Farmer", "text"),
      c("pdnQty", "PDN Qty", "qty", { total: true }),
      c("arrivalQty", "Arrival Qty", "qty", { total: true }),
      c("processedOn", "Date of Processing", "date"),
      c("gotResult", "GOT Result", "text", { align: "center" }),
      c("dogr", "DOGR", "date", { blank: true }),
      c("physicalPurityPct", "Physical Purity", "pct"),
      c("germinationPct", "Germination %", "pct"),
      c("geneticPurityPct", "Genetic Purity %", "pct", { blank: true }),
    ],
    tooMany: "Too many lots. Shorten the period or choose a crop.",
  },
};

// ---------------------------------------------------------------- CSW reports

/**
 * The CSW (Condition Seed Warehouse) Reports tab, in the legacy menu order (wmsd csw/index1.php,
 * read 2026-10-16; columns from manual p345–353, docs/reference/seedtrac-manual-map.md §9.3).
 * Unidentified and Coded Condition Seed are commented out of the live menu, so they are not built.
 * Keys carry a `csw-` prefix so they never collide with the RSW report of the same name; the URL
 * drops it (`/reports/csw/quality`).
 */
export const CSW_REPORT_KEYS = [
  "csw-quality",
  "csw-crop-variety",
  "csw-substandard",
  "csw-reserve",
  "csw-warehouse-wise",
  "csw-daily-qc",
  "csw-qc-ageing",
  "csw-daily-got",
] as const;
export type CswReportKey = (typeof CSW_REPORT_KEYS)[number];

export function isCswReportKey(k: string): k is CswReportKey {
  return (CSW_REPORT_KEYS as readonly string[]).includes(k);
}

/** `/reports/csw/$slug` ↔ key. */
export const cswReportSlug = (k: CswReportKey) => k.slice(4);
export const cswReportKeyOf = (slug: string): CswReportKey | null => {
  const k = `csw-${slug}`;
  return isCswReportKey(k) ? k : null;
};

const CSW_NOTE = GOT_NOTE;
const R = WAREHOUSE_REPORTS;

export const CSW_REPORTS: {
  [K in CswReportKey]: ReportDef & { key: K };
} = {
  "csw-quality": {
    ...R.quality,
    key: "csw-quality",
    menu: "Quality Based Condition Seed Report",
    title: "Quality Based Condition Seed Report",
    note: CSW_NOTE,
  },
  "csw-crop-variety": {
    ...R["crop-variety"],
    key: "csw-crop-variety",
    title: "Crop Variety wise Condition Seed Report",
    // CSW adds the Treatment Schema the Processing Slip applied, after GOT Grade; the Transaction
    // ID (user, 2026-10-23) and the PP figures (user, 2026-10-26) are RSW's only.
    columns: R["crop-variety"].columns.flatMap((col) =>
      col.key === "gotGrade"
        ? [col, c("treatment", "Treatment Schema", "text", { align: "center" })]
        : col.key === "docNo" || PP_FIGURE_KEYS.has(col.key)
          ? []
          : [col]
    ),
  },
  "csw-substandard": {
    ...R.substandard,
    key: "csw-substandard",
    title: "Crop Variety wise Substandard Condition Seed Report",
  },
  "csw-reserve": {
    ...R.reserve,
    key: "csw-reserve",
    menu: "Reserve Condition Seed Report",
  },
  "csw-warehouse-wise": { ...R["warehouse-wise"], key: "csw-warehouse-wise" },
  "csw-daily-qc": { ...R["daily-qc"], key: "csw-daily-qc" },
  "csw-qc-ageing": { ...R["qc-ageing"], key: "csw-qc-ageing" },
  "csw-daily-got": {
    ...R["daily-got"],
    key: "csw-daily-got",
    note: "Condition seed in stock whose lineage had a GOT result reviewed on this day.",
  },
};

/** Legacy CSW Reports tab order. */
export const CSW_REPORT_LIST: readonly (ReportDef & { key: CswReportKey })[] =
  CSW_REPORT_KEYS.map((k) => CSW_REPORTS[k]);

/** "More than N days since the last QC test" choices (legacy `dotage`). */
export const QC_AGE_DAYS = [30, 45, 60, 90, 120, 180, 270, 365] as const;

/**
 * How wide each column sits (rem on screen; proportions in the PDF), so every Crop | Variety block
 * lines its columns up with the others and dates and lot numbers never wrap mid-word.
 */
const WIDE: Record<string, number> = {
  srfNo: 10,
  sampleType: 9,
  serial: 2.75,
  lotNumber: 10.5,
  crop: 8,
  /** Stock reports' Var. Type ("Not set Total" on one line). */
  varType: 7.5,
  variety: 11,
  productionLocation: 9,
  productionPerson: 9,
  organiser: 11,
  farmer: 11,
  source: 14,
  sourceType: 8,
  frnNo: 7.5,
  purpose: 6,
  reference: 8,
  reservedBy: 8,
  remarks: 10,
  reserveComment: 14,
  treatment: 6,
  subBin: 4.5,
  stage: 6,
  daysSinceDot: 4.5,
  gotStatus: 7,
  gotResult: 6.5,
  duration: 7.5,
  days: 3.5,
  germinationPct: 5.75,
  geneticPurityPct: 5.75,
  // RSW Crop Variety wise's PP figures (user, 2026-10-26): kept narrow so SLOC keeps its room.
  ppPurePct: 3.75,
  ppInertPct: 3.75,
  ppLightPct: 3.75,
  ppDiscolouredPct: 3.75,
  ppCutPct: 3.75,
  ppSmallPct: 3.75,
  ppRoughPct: 3.75,
  ppWhitePct: 3.75,
  ppOcsPerKg: 3.5,
  ppOdvPerKg: 3.5,
  docNo: 8,
  sourcePlant: 12,
  vendor: 11,
  vendorVariety: 9,
  vendorLotNo: 8,
  plotNo: 4.5,
  state: 7,
  stnNo: 7,
  prelimMoist: 6,
  ups: 6.5,
  upstype: 4,
  packQcStatus: 5.5,
  validityDays: 5.5,
  mps: 4.5,
  pouches: 5,
  barcode: 8.5,
  container: 4.5,
  bin: 8,
  packagingStatus: 6.5,
  fromLot: 10.5,
  toLot: 10.5,
  fromVariety: 9,
  toVariety: 9,
};
const BY_FORMAT: Record<ReportCellFormat, number> = {
  text: 6,
  int: 4,
  qty: 6.5,
  pct: 5,
  date: 6.5,
  slocs: 13,
};
export const columnWidth = (c: ReportColumn): number =>
  c.writeIn ? 10 : (WIDE[c.key] ?? BY_FORMAT[c.format]);

/** Every report definition by key, RSW and the rest. */
export const REPORT_DEFS: Record<ReportKey, ReportDef> = {
  ...WAREHOUSE_REPORTS,
  ...CSW_REPORTS,
  ...ARRIVAL_REPORT_DEFS,
  ...PACKAGING_REPORTS,
  ...STOCK_REPORTS,
  ...HIGH_MOISTURE_REPORTS,
  ...QUALITY_REPORTS,
  ...DISPATCH_REPORTS,
};

/** A sectioned report's heading parts ("Crop", "Variety" unless the report names its own). */
export const sectionLabelsOf = (def: ReportDef): readonly [string, string] =>
  def.sectionLabels ?? ["Crop", "Variety"];

/**
 * The columns a result prints: its own when it built them (a pivot's one column per pack size), else
 * the definition's, less any its filters switched off.
 */
export const shownColumns = (
  def: ReportDef,
  r: Pick<ReportResult, "hidden" | "columns">
): readonly ReportColumn[] => {
  const cols = r.columns ?? def.columns;
  return r.hidden?.length
    ? cols.filter((col) => !r.hidden!.includes(col.key))
    : cols;
};

/** Legacy Reports tab order. */
export const WAREHOUSE_REPORT_LIST: readonly WarehouseReportDef[] =
  WAREHOUSE_REPORT_KEYS.map((k) => WAREHOUSE_REPORTS[k]);

/** Source rows a report may read before it asks for narrower filters. */
export const REPORT_SOURCE_CAP = 5000;
/** Body rows a PDF may print (the Gate Register's proven limit). */
export const REPORT_PDF_ROW_CAP = 3000;

// ---------------------------------------------------------------- variety filter

/**
 * The Variety filter: a variety id, or one of the two kinds of raw seed with no decoded variety
 * (blind-coded with an SP pair, or unidentified with neither). Those are sections of their own in
 * every report, so the filter must reach them.
 */
export const UNDECODED_VARIETIES = ["coded", "unidentified"] as const;
export type UndecodedVariety = (typeof UNDECODED_VARIETIES)[number];
export type ReportVariety = number | UndecodedVariety;

/** The label a lot with no variety prints under, matching Raw Seed Release and Inventory. */
export function undecodedVarietyLabel(
  crop: string,
  spMale: string | null | undefined,
  spFemale: string | null | undefined
): string {
  return spMale || spFemale ? `${crop} - Coded` : `${crop} - Unidentified`;
}

// ---------------------------------------------------------------- QC and GOT buckets

export type QcBucket = "ok" | "ut" | "bl" | "fail" | "none";

/**
 * Where each `lots.qc_status` lands in the OK / UT / Fail / No QC columns (owner defaults Q1,
 * 2026-10-02). Exhaustive on purpose: a new enum value fails the typecheck here instead of
 * silently counting as Fail.
 */
const QC_STATUS_BUCKET: Record<LotQcStatus, QcBucket> = {
  not_applicable: "none",
  under_testing: "ut",
  eligible: "ok",
  expired: "fail",
  pending_review: "fail",
  released: "fail",
  rejected: "fail",
  quarantined: "fail",
};

/**
 * The QC column a lot counts under. A germination verified Borderline and still waiting for the
 * supervisor's decision is BL. Otherwise a sample still in the lab (under testing, or sent back for
 * a retest) wins over whatever the lot last carried, so a lot is never both OK and on the bench.
 */
export function qcBucket(
  lotQcStatus: string,
  openLab: boolean,
  blPending = false
): QcBucket {
  if (blPending) return "bl";
  if (openLab) return "ut";
  return QC_STATUS_BUCKET[lotQcStatus as LotQcStatus] ?? "fail";
}

export type GotBucket = "nut" | "ok" | "ut" | "bl" | "fail";

/**
 * The GOT column a lot counts under, from its arrival-declared GOT (nexseed records no GOT result
 * yet). GOT not required is NUT; required lots follow their status; a lot that arrived with no GOT
 * data counts as under test (owner default Q2).
 */
export function gotBucket(
  type: string | null | undefined,
  status: string | null | undefined
): GotBucket {
  if (type === "not_required") return "nut";
  if (status === "ok") return "ok";
  if (status === "fail") return "fail";
  if (status === "bl") return "bl";
  return "ut";
}

/** How the grids print a GOT: "GOT-R OK", "GOT-NR UT", or "—" when the arrival gave none. */
export function gotLabel(
  type: string | null | undefined,
  status: string | null | undefined
): string {
  if (!type && !status) return "—";
  const t = type === "not_required" ? "GOT-NR" : "GOT-R";
  const s =
    status === "ok"
      ? "OK"
      : status === "fail"
        ? "Fail"
        : status === "under_testing"
          ? "UT"
          : status === "bl"
            ? "BL"
            : status === "rt"
              ? "RT"
              : "";
  return s ? `${t} ${s}` : t;
}

/** Worst first: a blend with one failed parent is failed, one borderline parent borderline. */
const GOT_SEVERITY: Record<GotBucket, number> = {
  fail: 4,
  bl: 3,
  ut: 2,
  ok: 1,
  nut: 0,
};
export function worstGot(buckets: readonly GotBucket[]): GotBucket | null {
  let worst: GotBucket | null = null;
  for (const b of buckets)
    if (worst === null || GOT_SEVERITY[b] > GOT_SEVERITY[worst]) worst = b;
  return worst;
}

/** The Substandard report's rule: failed QC or failed GOT. */
export function isSubstandard(qc: QcBucket, got: GotBucket): boolean {
  return qc === "fail" || got === "fail";
}

// ---------------------------------------------------------------- rows and results

export interface SlocCell {
  path: string;
  bags: number;
  qty: string;
}

export type ReportCell = string | number | null | SlocCell[];
export type ReportRow = Record<string, ReportCell>;

/** A cell as plain text: SLOC cells become "path | bags | qty" joined by "; " (the CSV form). */
export function reportCellText(v: ReportCell | undefined): string {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v))
    return v.map((s) => `${s.path} | ${s.bags} | ${s.qty}`).join("; ");
  return typeof v === "number" ? String(v) : v;
}

export interface ReportSection {
  /** Null for the single unheaded section of a flat or aggregated report. */
  crop: string | null;
  variety: string | null;
  /** When set, the section's whole heading in place of the two-part one ("Organiser: X"). */
  heading?: string;
  rows: ReportRow[];
  totals: Record<string, number>;
}

export interface ReportResult {
  key: ReportKey;
  title: string;
  kind: ReportKind;
  plant: string;
  /** "As on" date for stock reports, the day for Daily QC (YYYY-MM-DD, IST). */
  asOn: string | null;
  date: string | null;
  from: string | null;
  to: string | null;
  /** "ALL" or the chosen name, for the heading. */
  cropLabel: string;
  varietyLabel: string;
  /** Extra heading lines beside Crop and Variety, e.g. "Arrival types: …", "Stage: Raw". */
  scope?: string[];
  /** Column keys the filters switched off (CSW "With SLOC: No" drops SLOC). */
  hidden?: string[];
  /**
   * The columns this result prints when they depend on the data, in place of the definition's: the
   * Packaging pivots have one Qty column per pack size the period or stock holds.
   */
  columns?: ReportColumn[];
  sections: ReportSection[];
  totals: Record<string, number>;
  rowCount: number;
  /**
   * The crops and varieties this report could show with the crop/variety filters cleared, for the
   * filter dropdowns (no master permission needed, and nothing listed comes back empty).
   */
  options: ReportOptions;
  /**
   * An aggregated report's source rows, by each row's `rowKey` cell, so a click on a summed row
   * can list what went into it (Consolidated Arrival: its arrivals date by date, user 2026-10-18).
   */
  breakdown?: Record<string, ReportRow[]>;
}

export interface ReportOptions {
  crops: { id: number; name: string }[];
  /** `value` is a variety id, or "coded" / "unidentified" (see `ReportVariety`). */
  varieties: { cropId: number; value: string; name: string }[];
  /** Arrival reports: the names the legacy PM pickers list, from the plant's arrivals. */
  organisers?: string[];
  states?: string[];
  prodLocations?: string[];
  personnel?: string[];
  sourcePlants?: { id: number; name: string }[];
  vendors?: { id: number; name: string }[];
  /** Packaging reports: the pack sizes (UPS) the report's rows could hold, for the UPS picker. */
  packSizes?: { id: number; name: string }[];
  /** Stock reports: the verticals (crop categories) the plant's stock covers. */
  verticals?: { id: number; name: string }[];
  /** Stock reports: the vertical the report ran on ("all" or its id), for the picker's default. */
  vertical?: string;
  /** Periodical Dispatch: the plants the reader's dispatches went to. */
  plants?: { id: number; name: string }[];
  /** Stock reports: the classifications the stock's varieties carry; `value` is an id or "none". */
  classifications?: { value: string; name: string }[];
  /** CSW Warehouse wise only: the plant's WHs, the chosen WH's bins and the chosen bin's sub-bins. */
  locations?: {
    id: number;
    parentId: number | null;
    code: string;
    kind: "wh" | "bin" | "sub_bin";
  }[];
}

/** Sum the total columns of some rows. Quantities keep three decimals. */
export function sumTotals(
  def: Pick<ReportDef, "columns">,
  rows: readonly ReportRow[]
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const col of def.columns) {
    if (!col.total) continue;
    let s = 0;
    for (const r of rows) {
      const v = r[col.key];
      const n =
        typeof v === "number" ? v : typeof v === "string" ? Number(v) : 0;
      if (Number.isFinite(n)) s += n;
    }
    out[col.key] = col.format === "qty" ? Math.round(s * 1000) / 1000 : s;
  }
  return out;
}

/**
 * Sections in report order: crop A→Z, then variety A→Z with the undecoded kinds last (Coded, then
 * Unidentified), each section's rows in the order given.
 */
export function sectionRows(
  def: ReportDef,
  rows: readonly (ReportRow & { crop: string; variety: string })[]
): ReportSection[] {
  const by = new Map<string, ReportSection>();
  for (const r of rows) {
    const k = `${r.crop}\u0000${r.variety}`;
    let s = by.get(k);
    if (!s) {
      s = { crop: r.crop, variety: r.variety, rows: [], totals: {} };
      by.set(k, s);
    }
    s.rows.push(r);
  }
  const sections = [...by.values()].sort(
    (a, b) =>
      (a.crop ?? "").localeCompare(b.crop ?? "") ||
      varietyRank(a.variety) - varietyRank(b.variety) ||
      (a.variety ?? "").localeCompare(b.variety ?? "")
  );
  for (const s of sections) {
    s.rows.forEach((r, i) => (r.serial = i + 1));
    s.totals = sumTotals(def, s.rows);
  }
  return sections;
}

function varietyRank(v: string | null): number {
  if (!v) return 0;
  if (v.endsWith(" - Coded")) return 1;
  if (v.endsWith(" - Unidentified")) return 2;
  return 0;
}

/** Same crop/variety order for the unsectioned reports' rows. */
export function compareCropVariety(
  a: { crop: string; variety: string },
  b: { crop: string; variety: string }
): number {
  return (
    a.crop.localeCompare(b.crop) ||
    varietyRank(a.variety) - varietyRank(b.variety) ||
    a.variety.localeCompare(b.variety)
  );
}

/**
 * Whole years, months and days from one ISO date to another, printed "7y 1m 1d". Months count by
 * anniversary (31 Jan + 1 month = 28/29 Feb), and the days are what is left after them.
 */
export function durationYmd(from: string, to: string): string {
  if (to <= from) return "0y 0m 0d";
  const [fy, fm, fd] = ymd(from);
  const [ty, tm] = ymd(to);
  let months = (ty - fy) * 12 + (tm - fm);
  const anchor = (n: number) => {
    const y = fy + Math.floor((fm + n) / 12);
    const m = (fm + n) % 12;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return Date.UTC(y, m, Math.min(fd, last));
  };
  const end = Date.UTC(...ymd(to));
  if (anchor(months) > end) months -= 1;
  const days = Math.round((end - anchor(months)) / 86_400_000);
  return `${Math.floor(months / 12)}y ${months % 12}m ${days}d`;
}

/** Days between two ISO dates (to − from). */
export function daysBetween(from: string, to: string): number {
  const a = Date.UTC(...ymd(from));
  const b = Date.UTC(...ymd(to));
  return Math.round((b - a) / 86_400_000);
}

function ymd(s: string): [number, number, number] {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return [y, m - 1, d];
}
